'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Upload, Video, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import axiosInstance from '@/lib/axios';
import { useParams, useNavigate } from 'react-router-dom';
import { FormTextareaField } from '@/components/forms/form-textarea-field';
import { FormInputField } from '@/components/forms/form-input-field';
import { FileAttachmentList } from '@/components/forms/file-attachment-list';
import { FileUploadButton } from '@/components/forms/file-upload-button';
import { SectionLabel } from '@/components/forms/section-label';
import { FormSubmitOverlay } from '@/components/forms/form-submit-overlay';
import { uploadFilesToVertex } from '@/lib/vertex-upload';

const formSchema = z.object({
  content: z.string().min(1, 'Please write something...').max(2000),
  location: z.string().optional(),
});

export function GeneralPostForm() {
  const { communityId } = useParams<{ communityId: string }>();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [images, setImages] = useState<File[]>([]);
  const [videos, setVideos] = useState<File[]>([]);
  const [submitPhase, setSubmitPhase] = useState<'idle' | 'uploading' | 'saving'>('idle');

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      content: '',
      location: '',
    },
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    if (!communityId) return;
    setIsSubmitting(true);
    const hasFiles = images.length > 0 || videos.length > 0;
    setSubmitPhase(hasFiles ? 'uploading' : 'saving');

    try {
      const imageUrls = images.length > 0 ? await uploadFilesToVertex(images, 'posts/images') : [];
      const videoUrls = videos.length > 0 ? await uploadFilesToVertex(videos, 'posts/videos') : [];

      const formData = new FormData();
      formData.append('content', values.content);
      if (values.location) formData.append('location', values.location);
      formData.append('imageUrls', JSON.stringify(imageUrls));
      formData.append('videoUrls', JSON.stringify(videoUrls));

      setSubmitPhase('saving');
      const { data } = await axiosInstance.post(`/api/communities/${communityId}/posts`, formData);
      toast.success('Post created successfully!');
      navigate(`/${communityId}/dashboard?postid=${data.postId}`);
    } catch (error) {
      console.error('Failed to create post', error);
      toast.error('Failed to create post. Please try again.');
    } finally {
      setIsSubmitting(false);
      setSubmitPhase('idle');
    }
  };

  const removeFile = (
    index: number,
    setFileState: React.Dispatch<React.SetStateAction<File[]>>
  ) => {
    setFileState((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <>
      <FormSubmitOverlay
        open={isSubmitting}
        title={submitPhase === 'uploading' ? 'Uploading media...' : 'Publishing post...'}
        description={
          submitPhase === 'uploading'
            ? 'Sending images and videos through secure media upload. Please keep this screen open.'
            : 'Saving your post.'
        }
      />
      <Card className="w-full max-w-5xl mx-auto border-0 md:border md:border-border bg-background/50 backdrop-blur-md shadow-none md:shadow-2xl animate-in slide-in-from-bottom-5 duration-300">
        <CardHeader className="px-3 md:px-6">
          <CardTitle className="text-2xl font-black tracking-tight text-foreground">
            Create Post
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            Share updates, news, or thoughts with the community.
          </CardDescription>
        </CardHeader>
        <CardContent className="px-3 md:px-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormTextareaField
                control={form.control}
                name="content"
                label="What's on your mind?"
                placeholder="Type your message here... Use @ for a member or business, # for a category"
                required
                mentions
                className="min-h-[150px]"
              />

              <FormInputField
                control={form.control}
                name="location"
                label="Location (Optional)"
                placeholder="Where is this happening?"
                icon={<MapPin className="h-4 w-4 text-muted-foreground" />}
              />

              <div className="space-y-4">
                <div className="flex gap-4 flex-wrap">
                  {/* Image Upload */}
                  <div className="flex-1 min-w-[140px]">
                    <SectionLabel className="mb-2">Images</SectionLabel>
                    <FileUploadButton
                      id="post-images"
                      accept="image/*"
                      onFiles={(files) => setImages((prev) => [...prev, ...files])}
                      label="Add Images"
                      icon={Upload}
                      className="w-full"
                    />
                    <div className="mt-2">
                      <FileAttachmentList
                        files={images}
                        onRemove={(i) => removeFile(i, setImages)}
                      />
                    </div>
                  </div>

                  {/* Video Upload */}
                  <div className="flex-1 min-w-[140px]">
                    <SectionLabel className="mb-2">Videos</SectionLabel>
                    <FileUploadButton
                      id="post-videos"
                      accept="video/*"
                      onFiles={(files) => setVideos((prev) => [...prev, ...files])}
                      label="Add Videos"
                      icon={Video}
                      className="w-full"
                    />
                    <div className="mt-2">
                      <FileAttachmentList
                        files={videos}
                        onRemove={(i) => removeFile(i, setVideos)}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-6 border-t border-border">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => navigate(`/${communityId}/create`)}
                  disabled={isSubmitting}
                  className="text-muted-foreground hover:text-foreground hover:bg-muted"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!form.formState.isValid || isSubmitting}
                  className="bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/20 px-8"
                >
                  {isSubmitting ? 'Posting...' : 'Post'}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </>
  );
}
